import { API_CONFIG } from '../utils/apiConfig';
import { getAccessToken } from '../utils/apiClient';

const DEFAULT_BASE_URL = API_CONFIG.PROFILE_API_URL;

export interface ProfileServiceConfig {
    baseUrl?: string;
    accessToken?: string;
}

export interface TherapyProfile {
    therapy: {
        week: number;
        date: string;
    };
    manual_rewards: boolean;
    questionnaire_energy: number;
    expo_energy: number;
    nachsorge_energy: number;
    questionnaire_stars: number;
    expo_stars: number;
    nachsorge_stars: number;
    questionnaire_energy_per_year: number;
    expo_energy_per_year: number;
    nachsorge_energy_per_year: number;
    questionnaire_stars_per_year: number;
    expo_stars_per_year: number;
    nachsorge_stars_per_year: number;
    fallback_age: number;
}

export interface RewardSettings {
    questionnaire_energy: number;
    expo_energy: number;
    nachsorge_energy: number;
    questionnaire_stars: number;
    expo_stars: number;
    nachsorge_stars: number;
}

export class ProfileApiException extends Error {
    public readonly status: number;
    public readonly missingScopes?: string[];

    constructor(message: string, status: number, missingScopes?: string[]) {
        super(message);
        this.name = 'ProfileApiException';
        this.status = status;
        this.missingScopes = missingScopes;
    }
}

async function request<T>(
    method: 'GET' | 'POST' | 'PUT',
    path: string,
    config?: ProfileServiceConfig,
    requestBody?: unknown,
): Promise<T> {
    const baseUrl = config?.baseUrl || DEFAULT_BASE_URL;
    if (!baseUrl) {
        throw new Error('Missing API base URL for Profile service');
    }

    const accessToken = config?.accessToken ?? getAccessToken();
    if (!accessToken) {
        throw new Error('Access token is required for profile requests');
    }

    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${normalizedBase}${normalizedPath}`;

    let response: Response;
    try {
        response = await fetch(url, {
            method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: requestBody === undefined ? undefined : JSON.stringify(requestBody),
        });
    } catch (networkError) {
        throw networkError;
    }

    if (!response.ok) {
        const responseText = await response.text();
        let message = response.statusText || 'Request failed';
        let missingScopes: string[] | undefined;

        if (responseText) {
            try {
                const parsed = JSON.parse(responseText) as {
                    message?: string;
                    error?: string;
                    missingScopes?: string[];
                };
                message = parsed.message || parsed.error || responseText;
                if (Array.isArray(parsed.missingScopes)) {
                    missingScopes = parsed.missingScopes;
                }
            } catch {
                message = responseText;
            }
        }

        if (!missingScopes && message.includes('missing required scope:')) {
            const parsedScope = message.split('missing required scope:').pop()?.trim();
            if (parsedScope) {
                missingScopes = [parsedScope];
            }
        }

        throw new ProfileApiException(message, response.status, missingScopes);
    }

    const body = await response.text();
    if (!body || body.trim() === '') {
        return undefined as T;
    }

    return JSON.parse(body) as T;
}

export async function getProfile(id: string, config?: ProfileServiceConfig): Promise<TherapyProfile> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    return request<TherapyProfile>('GET', `/profile/${encodeURIComponent(id)}`, config);
}

export async function initProfile(id: string, config?: ProfileServiceConfig): Promise<void> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    await request<void>('POST', `/profile/${encodeURIComponent(id)}/init`, config);
}

export async function updateRewards(
    id: string,
    settings: RewardSettings,
    config?: ProfileServiceConfig,
): Promise<RewardSettings> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    return request<RewardSettings>('PUT', `/profile/${encodeURIComponent(id)}/energy`, config, settings);
}

export async function resetRewards(id: string, config?: ProfileServiceConfig): Promise<void> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    await request<void>('PUT', `/profile/${encodeURIComponent(id)}/energy/reset`, config);
}

export async function nextWeek(id: string, config?: ProfileServiceConfig): Promise<void> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    await request<void>('POST', `/next-week/${encodeURIComponent(id)}`, config);
}

export async function previousWeek(id: string, config?: ProfileServiceConfig): Promise<void> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    await request<void>('POST', `/previous-week/${encodeURIComponent(id)}`, config);
}

export async function startNachsorge(id: string, config?: ProfileServiceConfig): Promise<void> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    await request<void>('POST', `/start-nachsorge/${encodeURIComponent(id)}`, config);
}

export async function resetNachsorge(id: string, config?: ProfileServiceConfig): Promise<void> {
    if (!id) {
        throw new Error('Profile id is required');
    }
    await request<void>('POST', `/reset-nachsorge/${encodeURIComponent(id)}`, config);
}
